import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'
import { apiFetch } from '../lib/api'
import { toast } from '../lib/toast'

type Vote = 'up' | 'down' | null

// The prototype's 15px thumbs-up / thumbs-down glyphs inside `.vote-btn`.
function ThumbsUpIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
    </svg>
  )
}
function ThumbsDownIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
    </svg>
  )
}

const VOTE_BTN_CLASS =
  'inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-lg border text-[13px] cursor-pointer transition-colors'
const VOTE_BTN_STATE_CLASS = {
  voted: 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent-text)]',
  idle: 'border-[var(--border)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:border-[var(--accent)]',
}

// Shared shell between ComingSoonPreview and NotContractedPreview — both
// match the prototype's generic `.not-subscribed-state` (the same panel Agent
// Studio's Global Settings › Connections uses), differing only in the badge/
// title/description/pill *content*, never the markup. Geometry is the
// prototype CSS (subscription-portal.html `.not-subscribed-state`,
// `.ns-badge`, `.req-badge`, `.vote-btns`, `.vote-btn`): 60px 20px padding,
// a 3px 10px badge 14px above the 14px/600 title (4px 0 margins), a 13px
// description in the inherited primary colour, a 5px 14px lock pill 14px
// below it, and the thumbs-up / thumbs-down vote buttons (7px 14px, 8px
// radius, 15px glyphs) 18px below that. Callers place it in a Card/CardBody,
// as every prototype preview panel sits in `.card > .card-body`. Vote
// behaviour matches the prototype's initNotContractedPreview(): a click
// toggles that button's voted state, clicking the other switches it,
// clicking the already-voted one again clears it.
//
// Recording the vote is Agent Studio's (PreviewInterestVote.tsx), at the
// user's request (ARCHITECTURE_DECISIONS.md §64): a fresh vote — the
// prototype's own `console.log('[sales-signal] …')` point, i.e. a click that
// sets or switches the vote — posts `{feature, direction}` to
// POST /api/v1/preview-interest-vote, and once it settles, successfully or
// not, the toast thanks the user (the vote is advisory: a transport failure
// is not surfaced as one). Clearing a vote posts nothing; the event has no
// "clear" direction, in Studio either.
export function VotePreviewCard({
  badgeText,
  title,
  description,
  pillText,
  feature,
  featureName,
  descriptionMaxWidthClassName = 'max-w-[520px]',
}: Readonly<{
  badgeText: string
  title: ReactNode
  description: ReactNode
  pillText: string
  /** The analytics key the vote records (lib/previewVoteFeature.ts) — English,
   * never localised, never rendered. */
  feature: string
  /** What the thank-you toast calls the feature: the product name, or the
   * page's translated title. */
  featureName: string
  /** 480px for a product preview, 520px (default) for a coming-soon page —
   * the prototype's inline `max-width` on each. */
  descriptionMaxWidthClassName?: string
}>) {
  const { t } = useTranslation()
  const [vote, setVote] = useState<Vote>(null)

  const record = useMutation({
    mutationFn: (direction: Exclude<Vote, null>) =>
      apiFetch<void>('/api/v1/preview-interest-vote', {
        method: 'POST',
        body: JSON.stringify({ feature, direction }),
      }),
    onSettled: () => toast(t('notContracted.voteThanksToast', { feature: featureName })),
  })

  function castVote(v: Exclude<Vote, null>) {
    const clearing = vote === v
    setVote(clearing ? null : v)
    if (!clearing) record.mutate(v)
  }

  return (
    <div className="py-[60px] px-5 text-center">
      <span className="inline-block mb-3.5 text-[11px] font-semibold px-2.5 py-[3px] rounded-full bg-[var(--accent-subtle)] text-[var(--accent-text)]">
        {badgeText}
      </span>
      <p className="text-[14px] font-semibold my-1 text-[var(--text-primary)]">{title}</p>
      <p className={`text-[13px] text-[var(--text-primary)] ${descriptionMaxWidthClassName} mx-auto`}>{description}</p>
      <span className="inline-flex items-center gap-1.5 mt-3.5 text-[12px] font-semibold px-3.5 py-[5px] rounded-full bg-[var(--accent-subtle)] text-[var(--accent-text)]">
        <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        {pillText}
      </span>
      <div className="flex gap-2.5 justify-center mt-[18px]">
        <button
          type="button"
          aria-pressed={vote === 'up'}
          onClick={() => castVote('up')}
          className={`${VOTE_BTN_CLASS} ${vote === 'up' ? VOTE_BTN_STATE_CLASS.voted : VOTE_BTN_STATE_CLASS.idle}`}
        >
          <ThumbsUpIcon />
          {t('notContracted.interested')}
        </button>
        <button
          type="button"
          aria-pressed={vote === 'down'}
          onClick={() => castVote('down')}
          className={`${VOTE_BTN_CLASS} ${vote === 'down' ? VOTE_BTN_STATE_CLASS.voted : VOTE_BTN_STATE_CLASS.idle}`}
        >
          <ThumbsDownIcon />
          {t('notContracted.notForUs')}
        </button>
      </div>
    </div>
  )
}
